import { IInsightsWorkspaceOption } from "v2/services/insights.service";

export interface IWorkspaceGroup {
  buyerOrganizationId: string;
  buyerLabel: string;
  workspaces: IInsightsWorkspaceOption[];
}

/**
 * Agrupa por buyer **sem colapsar** os workspaces.
 *
 * O seletor antigo do chat mostrava uma opção por buyer, e ali isso bastava: o
 * backend resolve o CNPJ do buyer e qualquer workspace do grupo consulta o
 * mesmo dado. Para os relatórios não basta — o registry lê
 * `DBCONDUCCO."{workspace_id}"`, um schema por workspace. Colapsar deixaria o
 * segundo workspace de um buyer inalcançável, e mais da metade dos buyers tem
 * dois ou mais (produtos/operadoras diferentes, ambos ativos). Por isso o
 * `ContractSelector` — o mesmo nas duas abas — agrupa mas não colapsa.
 */
export function groupWorkspacesByBuyer(
  workspaces: IInsightsWorkspaceOption[]
): IWorkspaceGroup[] {
  const groups: IWorkspaceGroup[] = [];
  const byBuyer = new Map<string, IWorkspaceGroup>();

  workspaces.forEach((workspace) => {
    // Sem `buyerOrganizationId` o workspace vira grupo próprio: melhor um
    // cabeçalho a mais que juntar workspaces de buyers diferentes.
    const key = workspace.buyerOrganizationId || `ws:${workspace.id}`;
    const existing = byBuyer.get(key);
    if (existing) {
      existing.workspaces.push(workspace);
      return;
    }
    const group: IWorkspaceGroup = {
      buyerOrganizationId: workspace.buyerOrganizationId,
      buyerLabel: workspace.buyerName || workspace.label,
      workspaces: [workspace],
    };
    byBuyer.set(key, group);
    groups.push(group);
  });

  // Filiais compartilham o `corporateName` mas são orgs distintas: só desambigua
  // com CNPJ quando o nome de fato repete.
  const nameCount = new Map<string, number>();
  groups.forEach((group) =>
    nameCount.set(group.buyerLabel, (nameCount.get(group.buyerLabel) ?? 0) + 1)
  );
  groups.forEach((group) => {
    const first = group.workspaces[0];
    if ((nameCount.get(group.buyerLabel) ?? 0) > 1 && first.buyerFederalTaxId) {
      group.buyerLabel = `${group.buyerLabel} · ${first.buyerFederalTaxId}`;
    }
  });

  return groups;
}

/** O rótulo de um workspace na lista. `label` já traz produto e contrato. */
export function workspaceLabel(workspace: IInsightsWorkspaceOption): string {
  return workspace.label || workspace.id;
}

export function findWorkspace(
  workspaces: IInsightsWorkspaceOption[],
  workspaceId: string
): IInsightsWorkspaceOption | undefined {
  return workspaces.find((workspace) => workspace.id === workspaceId);
}
